import { getText } from "shared/lib";
import {
  TreePropertyType,
  updateTreeProperty,
  type TreeDescriptor,
  type HeightTreePropertyDescriptor,
} from "../lib";
import { useTreeDialogValidator } from "./lib";

import { useIsRTL } from "@/hooks";

import { Input2, Slider } from "shared/components";
import { FieldLabel } from "./FieldLabel";
import { FieldValidationMessages } from "./FieldValidationMessages";

export interface HeightFieldProps {
  onChange: ((treeDescriptor: TreeDescriptor) => void) | undefined;
  treeDescriptor: TreeDescriptor;
}

export function HeightField<T extends HeightTreePropertyDescriptor>({
  onChange,
  treeDescriptor,
}: HeightFieldProps) {
  const isRTL = useIsRTL();

  const { isValid, messages } = useTreeDialogValidator<T>({
    checker: (property) => !!property?.value,
    properties: treeDescriptor.properties,
    treePropertyType: TreePropertyType.height,
  });

  const heightProperty = treeDescriptor.properties.get(
    TreePropertyType.height,
  ) as T | undefined;

  const handleChange = () => (updater: (draft: T) => void) => {
    const newTreeDescriptor = updateTreeProperty<T>(
      treeDescriptor,
      TreePropertyType.height,
      updater,
    );

    onChange?.(newTreeDescriptor);
  };

  return (
    <div className="flex flex-col gap-1">
      <FieldLabel isValid={isValid}>{getText("Height")}</FieldLabel>

      <div className="flex items-center gap-2">
        <Slider
          defaultValue={[2]}
          max={12.0}
          min={2.0}
          step={0.05}
          onValueChange={(value) => {
            handleChange()((draft) => {
              draft.value = parseFloat(value[0].toString())
                .toFixed(2)
                .toString();
            });
          }}
          value={[heightProperty?.value ? parseFloat(heightProperty.value) : 0]}
        />

        <Input2
          className="text-bodymd placeholder:text-bodymd w-15 min-w-15 not-rtl:pl-1 not-rtl:pr-0 relative h-5 pr-0 rtl:pl-0 rtl:pr-1"
          {...(isRTL
            ? {
                startClassName:
                  "border border-interactiveBorder bg-interfaceBgDarkPaper absolute left-[-1px] top-[-1px] bottom-[-1px] px-1 rounded-l-sm text-bodymd",
                startContent: "m",
              }
            : {
                endClassName:
                  "border border-interactiveBorder bg-interfaceBgDarkPaper absolute right-[-1px] top-[-1px] bottom-[-1px] px-1 rounded-r-sm text-bodymd",
                endContent: "m",
              })}
          max={12.0}
          min={2}
          step={0.05}
          onChange={(event) => {
            handleChange()((draft) => {
              draft.value = event.target.value;
            });
          }}
          rounded="sm"
          type="number"
          value={heightProperty?.value || ""}
        />
      </div>

      <FieldValidationMessages messages={messages} />
    </div>
  );
}
